import { useTranslation } from 'react-i18next';
import { Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Menu, MenuRecipeSummary } from '../types';

interface CostingTabProps {
  menu: Menu;
  recipeSummaries: MenuRecipeSummary[];
}

/** Margin per dish, computed from the dish's menu price against the recipe's costPerPortion
 * (sourced live from RecipeV2 via useMenuDetail's recipeSummaries, not duplicated here). */
const CostingTab = ({ menu, recipeSummaries }: CostingTabProps) => {
  const { t } = useTranslation('menus');
  const dishes = menu.courses.flatMap((c) => c.dishes);

  if (dishes.length === 0) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('noDishes', 'This menu has no dishes to cost yet.')}
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('dish', 'Dish')}</TableCell>
            <TableCell align="right">{t('price', 'Price')}</TableCell>
            <TableCell align="right">{t('costPerPortion', 'Cost / Portion')}</TableCell>
            <TableCell align="right">{t('marginPercent', 'Margin %')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dishes.map((dish) => {
            const summary = recipeSummaries.find((s) => s.recipeId === dish.recipeId);
            const cost = summary?.costPerPortion;
            const margin = cost !== undefined && dish.price > 0
              ? ((dish.price - cost) / dish.price) * 100
              : null;
            return (
              <TableRow key={dish.recipeId}>
                <TableCell>{summary?.recipeName ?? t('unknownRecipe', 'Unknown recipe')}</TableCell>
                <TableCell align="right">£{dish.price.toFixed(2)}</TableCell>
                <TableCell align="right">{cost !== undefined ? `£${cost.toFixed(2)}` : '-'}</TableCell>
                <TableCell align="right">
                  {margin !== null ? (
                    <Chip
                      label={`${margin.toFixed(0)}%`}
                      size="small"
                      color={margin >= menu.targetMarginPercent ? 'success' : 'warning'}
                    />
                  ) : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CostingTab;
